import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import SelectInput from "../../../../components/inputs/SelectInput";
import FractionInput, {
  parseFractionLikeInput as parseFraction,
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";

const pick = (items) => items[Math.floor(Math.random() * items.length)];

const stripUnits = (raw) =>
  String(raw || "")
    .toLowerCase()
    .replace(/[^\d.,/-]+/g, "")
    .trim();

const parseUserNumber = (raw) => parseFraction(stripUnits(raw));
const thirdAngle = (a, b) => 180 - a - b;
const sortedAngles = (angles) => [...angles].sort((x, y) => x - y);
const sameAngles = (first, second) =>
  sortedAngles(first).every((value, index) => value === sortedAngles(second)[index]);
const angleSetLatex = (angles) => sortedAngles(angles).map((angle) => `${angle}^\\circ`).join(",\\;");

export const TRIANGLE_SSS_SIMILAR_VARIANTS = [
  { ab: 3, bc: 4, ac: 5, kl: 6, lm: 8, km: 10, similar: true },
  { ab: 4, bc: 5, ac: 6, kl: 8, lm: 10, km: 12, similar: true },
  { ab: 5, bc: 7, ac: 8, kl: 10, lm: 14, km: 16, similar: true },
];

export const TRIANGLE_SSS_NOT_SIMILAR_VARIANTS = [
  // W każdym wariancie dwa ilorazy są równe, a trzeci celowo inny.
  { ab: 3, bc: 4, ac: 5, kl: 6, lm: 9, km: 10, similar: false },
  { ab: 4, bc: 5, ac: 6, kl: 8, lm: 10, km: 13, similar: false },
  { ab: 5, bc: 6, ac: 7, kl: 10, lm: 13, km: 14, similar: false },
];

export const TRIANGLE_ANGLE_SIMILAR_VARIANTS = [
  { a: 40, b: 75, k: 75, l: 65 },
  { a: 35, b: 85, k: 60, l: 85 },
  { a: 50, b: 60, k: 70, l: 60 },
];

export const TRIANGLE_ANGLE_NOT_SIMILAR_VARIANTS = [
  // Po obliczeniu trzecich kątów trójkąty mają dokładnie jeden wspólny kąt.
  { a: 42, b: 68, k: 42, l: 65 },
  { a: 30, b: 90, k: 55, l: 90 },
  { a: 48, b: 82, k: 48, l: 60 },
];

const pickFiftyFifty = (positiveVariants, negativeVariants) =>
  pick(Math.random() < 0.5 ? positiveVariants : negativeVariants);

function NumberInput({ userAnswer, onChange, disabled, label, suffixLatex, placeholder }) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  return (
    <FractionInput
      value={raw}
      onChange={(value) =>
        onChange({
          raw: value?.raw ?? "",
          parsed: parseUserNumber(value?.raw ?? ""),
        })
      }
      disabled={disabled}
      label={label}
      suffixLatex={suffixLatex}
      placeholder={placeholder}
    />
  );
}

function ChoiceLine({ label, children }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
      <strong>{label}.</strong>
      <span>{children}</span>
    </div>
  );
}

const CORRESPONDING_SIDES = {
  AB: "KL",
  BC: "LM",
  AC: "KM",
};

const CORRESPONDING_ANGLES = { A: "K", B: "L", C: "M" };
const ANGLE_COLORS = { A: "#f59e0b", B: "#06b6d4", C: "#a855f7" };

function angleArcPath(vertex, from, to, radius = 19) {
  const startAngle = Math.atan2(from.y - vertex.y, from.x - vertex.x);
  let delta = Math.atan2(to.y - vertex.y, to.x - vertex.x) - startAngle;
  while (delta > Math.PI) delta -= 2 * Math.PI;
  while (delta < -Math.PI) delta += 2 * Math.PI;
  const endAngle = startAngle + delta;
  const start = {
    x: vertex.x + radius * Math.cos(startAngle),
    y: vertex.y + radius * Math.sin(startAngle),
  };
  const end = {
    x: vertex.x + radius * Math.cos(endAngle),
    y: vertex.y + radius * Math.sin(endAngle),
  };
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 0 ${delta > 0 ? 1 : 0} ${end.x} ${end.y}`;
}

function SimilarityPairDiagram({
  layout = "mirrored",
  firstSide,
  secondSide,
  firstAngle,
  secondAngle,
  showAllAngles = false,
}) {
  const firstPoints = {
    A: { x: 35, y: 150 },
    B: { x: 215, y: 150 },
    C: { x: 92, y: 25 },
  };
  const secondPoints = layout === "mirrored"
    ? {
        K: { x: 520, y: 150 },
        L: { x: 325, y: 150 },
        M: { x: 450, y: 25 },
      }
    : {
        K: { x: 325, y: 150 },
        L: { x: 520, y: 150 },
        M: { x: 455, y: 25 },
      };
  const points = { ...firstPoints, ...secondPoints };
  const segment = (name) => [points[name[0]], points[name[1]]];
  const firstSegment = firstSide ? segment(firstSide) : null;
  const secondSegment = secondSide ? segment(secondSide) : null;
  const angleNeighbors = {
    A: ["B", "C"], B: ["A", "C"], C: ["A", "B"],
    K: ["L", "M"], L: ["K", "M"], M: ["K", "L"],
  };
  const angleColor = (name) => ANGLE_COLORS[
    name === "K" ? "A" : name === "L" ? "B" : name === "M" ? "C" : name
  ];
  const markedAngles = showAllAngles
    ? ["A", "B", "C", "K", "L", "M"]
    : [firstAngle, secondAngle].filter(Boolean);

  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "16px 0 8px" }}>
      <svg
        viewBox="0 0 555 185"
        role="img"
        aria-label="Dwa podobne trójkąty z zaznaczonymi odpowiadającymi elementami"
        style={{ width: "min(100%, 555px)", height: "auto", color: "var(--color-text, #f8fafc)" }}
      >
        <polygon points="35,150 215,150 92,25" fill="color-mix(in srgb, #f59e0b 10%, transparent)" stroke="currentColor" strokeWidth="3" />
        <polygon
          points={`${secondPoints.K.x},${secondPoints.K.y} ${secondPoints.L.x},${secondPoints.L.y} ${secondPoints.M.x},${secondPoints.M.y}`}
          fill="color-mix(in srgb, #8b5cf6 10%, transparent)"
          stroke="currentColor"
          strokeWidth="3"
        />
        {firstSegment ? <line x1={firstSegment[0].x} y1={firstSegment[0].y} x2={firstSegment[1].x} y2={firstSegment[1].y} stroke="#f59e0b" strokeWidth="7" strokeLinecap="round" /> : null}
        {secondSegment ? <line x1={secondSegment[0].x} y1={secondSegment[0].y} x2={secondSegment[1].x} y2={secondSegment[1].y} stroke="#8b5cf6" strokeWidth="7" strokeLinecap="round" /> : null}
        {markedAngles.map((name) => {
          const [from, to] = angleNeighbors[name];
          return (
            <path
              key={name}
              d={angleArcPath(points[name], points[from], points[to])}
              fill="none"
              stroke={angleColor(name)}
              strokeWidth="5"
              strokeLinecap="round"
            />
          );
        })}
        <g fill="currentColor" fontSize="22" fontStyle="italic">
          <text x="18" y="176">A</text><text x="220" y="176">B</text><text x="82" y="20">C</text>
          <text x={secondPoints.K.x + (layout === "mirrored" ? 8 : -22)} y="176">K</text>
          <text x={secondPoints.L.x + (layout === "mirrored" ? -22 : 8)} y="176">L</text>
          <text x={secondPoints.M.x - 10} y="20">M</text>
        </g>
      </svg>
    </div>
  );
}

const formatRatio = (numerator, denominator) => {
  const value = numerator / denominator;
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(".", "{,}");
};

const taskRegistry = {
  CorrespondingTriangleSideTask: {
    generate() {
      const firstSide = pick(Object.keys(CORRESPONDING_SIDES));
      const correctSide = CORRESPONDING_SIDES[firstSide];
      const choices = ["KL", "LM", "KM"];
      return {
        data: {
          layout: pick(["mirrored", "rotated"]),
          firstSide,
          correctSide,
          correctIndex: choices.indexOf(correctSide),
          options: choices.map((side, index) => ({
            label: ["A", "B", "C"][index],
            side,
          })),
        },
      };
    },
    renderTask({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.65, marginBottom: 16 }}>
          <p>
            Trójkąty są podobne i zapisano je w odpowiadającej kolejności:{" "}
            <InlineMath math={"\\triangle ABC\\sim\\triangle KLM"} />.
          </p>
          <p>
            Który bok trójkąta <InlineMath math="KLM" /> odpowiada bokowi{" "}
            <InlineMath math={data.firstSide} />?
          </p>
          <SimilarityPairDiagram layout={data.layout} firstSide={data.firstSide} />
          <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
            {data.options.map((option) => (
              <ChoiceLine key={option.label} label={option.label}>
                <InlineMath math={option.side} />
              </ChoiceLine>
            ))}
          </div>
        </div>
      );
    },
    renderInput({ data }, userAnswer, onChange, disabled) {
      return (
        <ABCDInput
          options={data.options.map((option, index) => ({
            value: index,
            label: option.label,
            textNode: option.label,
          }))}
          value={typeof userAnswer?.choiceIndex === "number" ? userAnswer.choiceIndex : null}
          onChange={(choiceIndex) => onChange({
            choiceIndex,
            side: data.options[choiceIndex]?.side,
          })}
          disabled={disabled}
        />
      );
    },
    checkAnswer({ data }, userAnswer) {
      return userAnswer?.choiceIndex === data.correctIndex;
    },
    renderAnswer({ data }) {
      return <InlineMath math={data.firstSide + "\\leftrightarrow " + data.correctSide} />;
    },
    renderUserAnswer(userAnswer, task) {
      const side = task?.data?.options?.[userAnswer?.choiceIndex]?.side;
      return side ? <InlineMath math={side} /> : <i>(brak odpowiedzi)</i>;
    },
    renderExplanation({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p>
            Z zapisu podobieństwa odczytujemy: <InlineMath math={"A\\leftrightarrow K"} />,{" "}
            <InlineMath math={"B\\leftrightarrow L"} /> oraz{" "}
            <InlineMath math={"C\\leftrightarrow M"} />. Łączymy więc odpowiadające
            sobie pary wierzchołków.
          </p>
          <SimilarityPairDiagram
            layout={data.layout}
            firstSide={data.firstSide}
            secondSide={data.correctSide}
            showAllAngles
          />
          <BlockMath math={data.firstSide + "\\leftrightarrow " + data.correctSide} />
        </div>
      );
    },
  },

  CorrespondingTriangleAngleTask: {
    generate() {
      const firstAngle = pick(Object.keys(CORRESPONDING_ANGLES));
      const correctAngle = CORRESPONDING_ANGLES[firstAngle];
      const choices = ["K", "L", "M"];
      return {
        data: {
          layout: pick(["mirrored", "rotated"]),
          firstAngle,
          correctAngle,
          correctIndex: choices.indexOf(correctAngle),
          options: choices.map((angle, index) => ({
            label: ["A", "B", "C"][index],
            angle,
          })),
        },
      };
    },
    renderTask({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.65, marginBottom: 16 }}>
          <p>
            Trójkąty zapisano w odpowiadającej kolejności: {" "}
            <InlineMath math="\\triangle ABC\\sim\\triangle KLM" />.
            Który kąt trójkąta <InlineMath math="KLM" /> odpowiada kątowi{" "}
            <InlineMath math={`\\angle ${data.firstAngle}`} />?
          </p>
          <SimilarityPairDiagram layout={data.layout} firstAngle={data.firstAngle} />
          <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
            {data.options.map((option) => (
              <ChoiceLine key={option.label} label={option.label}>
                <InlineMath math={`\\angle ${option.angle}`} />
              </ChoiceLine>
            ))}
          </div>
        </div>
      );
    },
    renderInput({ data }, userAnswer, onChange, disabled) {
      return (
        <ABCDInput
          options={data.options.map((option, index) => ({
            value: index,
            label: option.label,
            textNode: option.label,
          }))}
          value={typeof userAnswer?.choiceIndex === "number" ? userAnswer.choiceIndex : null}
          onChange={(choiceIndex) => onChange({ choiceIndex })}
          disabled={disabled}
        />
      );
    },
    checkAnswer({ data }, userAnswer) {
      return userAnswer?.choiceIndex === data.correctIndex;
    },
    renderAnswer({ data }) {
      return <InlineMath math={`\\angle ${data.firstAngle}\\leftrightarrow\\angle ${data.correctAngle}`} />;
    },
    renderUserAnswer(userAnswer, task) {
      const angle = task?.data?.options?.[userAnswer?.choiceIndex]?.angle;
      return angle ? <InlineMath math={`\\angle ${angle}`} /> : <i>(brak odpowiedzi)</i>;
    },
    renderExplanation({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p>
            Z kolejności liter otrzymujemy pary: <InlineMath math="A\\leftrightarrow K" />,{" "}
            <InlineMath math="B\\leftrightarrow L" /> i <InlineMath math="C\\leftrightarrow M" />.
            Na rysunku każda para odpowiadających kątów ma ten sam kolor.
          </p>
          <SimilarityPairDiagram
            layout={data.layout}
            firstAngle={data.firstAngle}
            secondAngle={data.correctAngle}
            showAllAngles
          />
          <BlockMath math={`\\angle ${data.firstAngle}\\leftrightarrow\\angle ${data.correctAngle}`} />
        </div>
      );
    },
  },

  TriangleSSSSimilarityCheckTask: {
    generate() {
      const variant = pickFiftyFifty(
        TRIANGLE_SSS_SIMILAR_VARIANTS,
        TRIANGLE_SSS_NOT_SIMILAR_VARIANTS
      );
      return {
        data: {
          ...variant,
          correctValue: variant.similar ? "yes" : "no",
        },
      };
    },
    renderTask({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.65, marginBottom: 16 }}>
          <p>Dane są długości boków dwóch trójkątów:</p>
          <BlockMath math={"|AB|=" + data.ab + ",\\quad |BC|=" + data.bc + ",\\quad |AC|=" + data.ac} />
          <BlockMath math={"|KL|=" + data.kl + ",\\quad |LM|=" + data.lm + ",\\quad |KM|=" + data.km} />
          <p>
            Czy trójkąty <InlineMath math="ABC" /> i <InlineMath math="KLM" /> są podobne?
          </p>
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      const value = typeof userAnswer === "string"
        ? userAnswer
        : userAnswer?.value ?? (userAnswer?.choiceIndex === 0 ? "yes" : userAnswer?.choiceIndex === 1 ? "no" : "");
      return (
        <div style={{ maxWidth: 320, marginTop: 10 }}>
          <SelectInput
            value={value}
            onChange={(event) => onChange({ value: event.target.value })}
            disabled={disabled}
            options={[
              { value: "yes", label: "Tak, są podobne" },
              { value: "no", label: "Nie, nie są podobne" },
            ]}
          />
        </div>
      );
    },
    checkAnswer({ data }, userAnswer) {
      const value = typeof userAnswer === "string"
        ? userAnswer
        : userAnswer?.value ?? (userAnswer?.choiceIndex === 0 ? "yes" : userAnswer?.choiceIndex === 1 ? "no" : "");
      return value === (data.correctValue || (data.similar ? "yes" : "no"));
    },
    renderAnswer({ data }) {
      return <span>{data.similar ? "Tak, trójkąty są podobne." : "Nie, trójkąty nie są podobne."}</span>;
    },
    renderUserAnswer(userAnswer) {
      const value = typeof userAnswer === "string"
        ? userAnswer
        : userAnswer?.value ?? (userAnswer?.choiceIndex === 0 ? "yes" : userAnswer?.choiceIndex === 1 ? "no" : "");
      return value ? <span>{value === "yes" ? "Tak, są podobne" : "Nie, nie są podobne"}</span> : <i>(brak odpowiedzi)</i>;
    },
    renderExplanation({ data }) {
      const first = formatRatio(data.kl, data.ab);
      const second = formatRatio(data.lm, data.bc);
      const third = formatRatio(data.km, data.ac);
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong> Porównujemy trzy pary odpowiadających sobie boków.</p>
          <BlockMath math={"\\frac{|KL|}{|AB|}=\\frac{" + data.kl + "}{" + data.ab + "}=" + first} />
          <BlockMath math={"\\frac{|LM|}{|BC|}=\\frac{" + data.lm + "}{" + data.bc + "}=" + second} />
          <BlockMath math={"\\frac{|KM|}{|AC|}=\\frac{" + data.km + "}{" + data.ac + "}=" + third} />
          {data.similar ? (
            <p>Wszystkie trzy ilorazy są równe, więc trójkąty są podobne z cechy bok–bok–bok.</p>
          ) : (
            <p>Ilorazy nie są wszystkie równe, więc trójkąty nie są podobne.</p>
          )}
        </div>
      );
    },
  },

  TriangleAngleSimilarityCheckTask: {
    generate() {
      const data = pickFiftyFifty(
        TRIANGLE_ANGLE_SIMILAR_VARIANTS,
        TRIANGLE_ANGLE_NOT_SIMILAR_VARIANTS
      );
      const anglesABC = [data.a, data.b, thirdAngle(data.a, data.b)];
      const anglesKLM = [data.k, data.l, thirdAngle(data.k, data.l)];
      const similar = sameAngles(anglesABC, anglesKLM);
      return {
        data: {
          ...data,
          c: thirdAngle(data.a, data.b),
          m: thirdAngle(data.k, data.l),
          similar,
          correctValue: similar ? "yes" : "no",
        },
      };
    },
    renderTask({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.65, marginBottom: 16 }}>
          <p>
            W trójkącie <InlineMath math="ABC" /> dane są kąty{" "}
            <InlineMath math={`\\angle A=${data.a}^\\circ`} /> oraz{" "}
            <InlineMath math={`\\angle B=${data.b}^\\circ`} />. W trójkącie{" "}
            <InlineMath math="KLM" /> dane są kąty <InlineMath math={`\\angle K=${data.k}^\\circ`} /> oraz{" "}
            <InlineMath math={`\\angle L=${data.l}^\\circ`} />.
          </p>
          <p>
            Czy trójkąty <InlineMath math="ABC" /> i <InlineMath math="KLM" /> są podobne?
          </p>
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      const value = typeof userAnswer === "string"
        ? userAnswer
        : userAnswer?.value ?? (userAnswer?.choiceIndex === 0 ? "yes" : userAnswer?.choiceIndex === 1 ? "no" : "");
      return (
        <div style={{ maxWidth: 320, marginTop: 10 }}>
          <SelectInput
            value={value}
            onChange={(event) => onChange({ value: event.target.value })}
            disabled={disabled}
            options={[
              { value: "yes", label: "Tak, są podobne" },
              { value: "no", label: "Nie, nie są podobne" },
            ]}
          />
        </div>
      );
    },
    checkAnswer({ data }, userAnswer) {
      const value = typeof userAnswer === "string"
        ? userAnswer
        : userAnswer?.value ?? (userAnswer?.choiceIndex === 0 ? "yes" : userAnswer?.choiceIndex === 1 ? "no" : "");
      return value === (data.correctValue || (data.similar ? "yes" : "no"));
    },
    renderAnswer({ data }) {
      return <span>{data.similar ? "Tak, trójkąty są podobne." : "Nie, trójkąty nie są podobne."}</span>;
    },
    renderUserAnswer(userAnswer) {
      const value = typeof userAnswer === "string"
        ? userAnswer
        : userAnswer?.value ?? (userAnswer?.choiceIndex === 0 ? "yes" : userAnswer?.choiceIndex === 1 ? "no" : "");
      return value ? <span>{value === "yes" ? "Tak, są podobne" : "Nie, nie są podobne"}</span> : <i>(brak odpowiedzi)</i>;
    },
    renderExplanation({ data }) {
      const abcAngles = [data.a, data.b, data.c];
      const klmAngles = [data.k, data.l, data.m];
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong> Obliczamy trzeci kąt w każdym trójkącie.</p>
          <BlockMath math={`\\angle C=180^\\circ-${data.a}^\\circ-${data.b}^\\circ=${data.c}^\\circ`} />
          <BlockMath math={`\\angle M=180^\\circ-${data.k}^\\circ-${data.l}^\\circ=${data.m}^\\circ`} />
          <p><strong>Krok 2.</strong> Porównujemy zbiory miar kątów.</p>
          <BlockMath math={`ABC:\\;\\{${angleSetLatex(abcAngles)}\\}\\qquad KLM:\\;\\{${angleSetLatex(klmAngles)}\\}`} />
          {data.similar ? (
            <p>
              Zbiory miar kątów są takie same, więc trójkąty są podobne z cechy kąt-kąt
              (równoważnie: kąt-kąt-kąt).
            </p>
          ) : (
            <p>
              Zbiory miar kątów nie są takie same, więc trójkąty nie są podobne.
            </p>
          )}
        </div>
      );
    },
  },

  // Kompatybilnosc ze starszymi zapisami podejsc: nowe losowanie nie uzywa juz tego typu.
  TriangleAAThirdAngleTask: {
    generate() {
      return {
        data: pick([
          { alpha: 50, beta: 60, gamma: 70 },
          { alpha: 40, beta: 75, gamma: 65 },
          { alpha: 30, beta: 90, gamma: 60 },
          { alpha: 55, beta: 65, gamma: 60 },
          { alpha: 48, beta: 82, gamma: 50 },
        ]),
      };
    },
    renderTask({ data }) {
      return (
        <div style={{ lineHeight: 1.65, marginBottom: 14 }}>
          W trójkącie <InlineMath math="ABC" /> dane są kąty{" "}
          <InlineMath math={`\\angle A=${data.alpha}^\\circ`} /> oraz{" "}
          <InlineMath math={`\\angle B=${data.beta}^\\circ`} />. Oblicz miarę trzeciego kąta{" "}
          <InlineMath math={String.raw`\angle C`} />.
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      return (
        <NumberInput
          userAnswer={userAnswer}
          onChange={onChange}
          disabled={disabled}
          label="\\gamma"
          suffixLatex={String.raw`^\circ`}
          placeholder="np. 70"
        />
      );
    },
    answer: ({ data }) => data.gamma,
    answerLabel: "\\gamma",
    answerSuffix: "^\\circ",
    renderAnswer: ({ data }) => <InlineMath math={`\\angle C=${data.gamma}^\\circ`} />,
    renderExplanation({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong> Suma miar kątów wewnętrznych trójkąta to <InlineMath math="180^\\circ" />.</p>
          <BlockMath math={`\\angle C=180^\\circ-${data.alpha}^\\circ-${data.beta}^\\circ=${data.gamma}^\\circ`} />
          <p>
            Taki rachunek wykorzystujemy przy cesze kąt–kąt: jeżeli dwa kąty
            drugiego trójkąta mają te same miary, trzeci także ma miarę{" "}
            <InlineMath math={`${data.gamma}^\\circ`} />.
          </p>
        </div>
      );
    },
  },

  SimilarTriangleSideTask: {
    generate() {
      return {
        data: pick([
          { ab: 3, kl: 6, k: 2, bc: 4, lm: 8 },
          { ab: 4, kl: 12, k: 3, bc: 5, lm: 15 },
          { ab: 5, kl: 10, k: 2, bc: 7, lm: 14 },
          { ab: 2, kl: 8, k: 4, bc: 3, lm: 12 },
          { ab: 6, kl: 18, k: 3, bc: 5, lm: 15 },
        ]),
      };
    },
    renderTask({ data }) {
      return (
        <div style={{ lineHeight: 1.65, marginBottom: 14 }}>
          Trójkąty <InlineMath math="ABC" /> i <InlineMath math="KLM" /> są podobne (
          <InlineMath math="ABC\\sim KLM" />). Bok <InlineMath math={`AB=${data.ab}\\,\\text{cm}`} />{" "}
          odpowiada bokowi <InlineMath math={`KL=${data.kl}\\,\\text{cm}`} />, a bok{" "}
          <InlineMath math={`BC=${data.bc}\\,\\text{cm}`} />. Oblicz długość odpowiadającego mu
          boku <InlineMath math="LM" />.
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      return (
        <NumberInput
          userAnswer={userAnswer}
          onChange={onChange}
          disabled={disabled}
          label="LM"
          suffixLatex={String.raw`\text{cm}`}
          placeholder="np. 8"
        />
      );
    },
    answer: ({ data }) => data.lm,
    answerLabel: "LM",
    answerSuffix: "\\,\\mathrm{cm}",
    renderAnswer: ({ data }) => <InlineMath math={`LM=${data.lm}\\,\\text{cm}`} />,
    renderExplanation({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong> Wyznaczamy skalę podobieństwa z odpowiadających sobie boków.</p>
          <BlockMath math={`k=\\frac{KL}{AB}=\\frac{${data.kl}}{${data.ab}}=${data.k}`} />
          <p><strong>Krok 2.</strong> Bok <InlineMath math="LM" /> jest <InlineMath math={`${data.k}`} /> razy dłuższy od <InlineMath math="BC" />.</p>
          <BlockMath math={`LM=${data.bc}\\cdot ${data.k}=${data.lm}\\,\\text{cm}`} />
        </div>
      );
    },
  },

  SimilarTrianglePerimeterTask: {
    generate() {
      return {
        data: pick([
          { a: 4, a2: 12, k: 3, perim: 20, big: 60 },
          { a: 5, a2: 10, k: 2, perim: 18, big: 36 },
          { a: 3, a2: 9, k: 3, perim: 14, big: 42 },
          { a: 6, a2: 12, k: 2, perim: 25, big: 50 },
          { a: 2, a2: 8, k: 4, perim: 9, big: 36 },
        ]),
      };
    },
    renderTask({ data }) {
      return (
        <div style={{ lineHeight: 1.65, marginBottom: 14 }}>
          Dwa trójkąty są podobne. Bok <InlineMath math={`${data.a}\\,\\text{cm}`} /> mniejszego
          trójkąta odpowiada bokowi <InlineMath math={`${data.a2}\\,\\text{cm}`} /> większego.
          Obwód mniejszego trójkąta wynosi <InlineMath math={`${data.perim}\\,\\text{cm}`} />.
          Oblicz obwód większego trójkąta.
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      return (
        <NumberInput
          userAnswer={userAnswer}
          onChange={onChange}
          disabled={disabled}
          label="Obw"
          suffixLatex={String.raw`\text{cm}`}
          placeholder="np. 60"
        />
      );
    },
    answer: ({ data }) => data.big,
    answerLabel: "Obw",
    answerSuffix: "\\,\\mathrm{cm}",
    renderAnswer: ({ data }) => <InlineMath math={`Obw=${data.big}\\,\\text{cm}`} />,
    renderExplanation({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong> Wyznaczamy skalę podobieństwa.</p>
          <BlockMath math={`k=\\frac{${data.a2}}{${data.a}}=${data.k}`} />
          <p><strong>Krok 2.</strong> Obwód figury podobnej rośnie w tej samej skali co długości boków.</p>
          <BlockMath math={`Obw=${data.perim}\\cdot ${data.k}=${data.big}\\,\\text{cm}`} />
        </div>
      );
    },
  },
};

// Kanon pozostaje szerszy niż przygotowanie maturalne, ale pomija robocze
// zadania polegające wyłącznie na wskazaniu odpowiadającego boku lub kąta.
// Te typy oraz SimilarTriangleSideTask pozostają w rejestrze dla zgodności
// ze starszymi zapisami podejść, lecz nie trafiają do nowych zestawów.
export const SIMILARITY_CANONICAL_TASK_TYPES = [
  "TriangleAngleSimilarityCheckTask",
  "TriangleSSSSimilarityCheckTask",
  "SimilarTrianglePerimeterTask",
];

function moduleFor(task) {
  const mod = taskRegistry[task?.type];
  if (!mod) throw new Error(`Nieznany typ zadania podobieństwa: ${task?.type}`);
  return mod;
}

export function generateTask(typeOrOptions) {
  const requestedType =
    typeof typeOrOptions === "string" ? typeOrOptions : typeOrOptions?.type;
  const taskType = requestedType || pick(SIMILARITY_CANONICAL_TASK_TYPES);
  return { type: taskType, ...taskRegistry[taskType].generate() };
}

export function generateTasks() {
  return SIMILARITY_CANONICAL_TASK_TYPES.map((type) => generateTask(type));
}

export const podobienstwoRegistry = Object.fromEntries(
  Object.keys(taskRegistry).map((type) => [
    type,
    {
      renderTask: (task) => renderTask({ ...task, type }),
      renderInput: (task, userAnswer, onChange, disabled) =>
        renderInput({ ...task, type }, userAnswer, onChange, disabled),
      checkAnswer: (task, userAnswer) => checkAnswer({ ...task, type }, userAnswer),
      renderAnswer: (task) => renderAnswer({ ...task, type }),
      renderExplanation: (payload) => renderExplanation({ ...payload, type }),
      renderUserAnswer: (userAnswer, task) => renderUserAnswer({ ...task, type }, userAnswer),
    },
  ])
);

export const registry = podobienstwoRegistry;

export function renderTask(task) {
  return moduleFor(task).renderTask(task);
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return moduleFor(task).renderInput(task, userAnswer, onChange, disabled);
}

export function checkAnswer(task, userAnswer) {
  const mod = moduleFor(task);
  if (mod.checkAnswer) return mod.checkAnswer(task, userAnswer);
  const expected = mod.answer(task);
  const parsed =
    typeof userAnswer === "object" && Number.isFinite(userAnswer?.parsed)
      ? userAnswer.parsed
      : parseUserNumber(typeof userAnswer === "string" ? userAnswer : userAnswer?.raw);
  return Number.isFinite(parsed) && Math.abs(parsed - expected) < 0.01;
}

export function renderAnswer(task) {
  return moduleFor(task).renderAnswer(task);
}

export function renderUserAnswer(task, userAnswer) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  const mod = moduleFor(task);
  if (mod.renderUserAnswer) return mod.renderUserAnswer(userAnswer, task);
  if (!raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`${formatLatexPreview(raw, mod.answerLabel)}${mod.answerSuffix}`} />;
}

export function renderExplanation(task) {
  return moduleFor(task).renderExplanation(task);
}
